import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, X, MapPin, Save, LayoutDashboard, ChevronRight, Zap, RefreshCw, Wand2, Home, Video, History, MessageCircle, CheckCircle, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@soVendor/lib/supabase';
import { 
  ResumeData, 
  JobData,
  BaselineScores, 
  OptimizationResult
} from './shared/types';
import UploadStep from './steps/UploadStep';
import TemplateStep from './steps/TemplateStep';
import ParsedResumeStep from './steps/ParsedResumeStep';
import JobUrlStep from './steps/JobUrlStep';
import BaselineAnalysisStep from './steps/BaselineAnalysisStep';
import OptimizationSettingsStep from './steps/OptimizationSettingsStep';
import ProcessingStep from './ProcessingStep';
import ResultsStep from './ResultsStep';
import SaveHistoryStep from './steps/SaveHistoryStep';
import ApplyStep from './steps/ApplyStep';
import { ApplyTrackModal } from '../optimize/ApplyTrackModal';
import ApplicationTrackingStep from './steps/ApplicationTrackingStep';
import NextActionModal, { NextActionPage } from './NextActionModal';
import { STEP_WRAPPER_CLASS, STEP_MOTION_CLASS, STEP_CARD_CLASS } from './shared/stepLayout';
import { setCompanionRecentContext } from '@soVendor/utils/companionContext';
import { OptimizerNavProvider } from './OptimizerNavContext';
import { useOptimizationProcess } from '@soVendor/hooks/useOptimizationProcess';
import { useCreditSystem } from '@soVendor/hooks/useCreditSystem';
import { analyzeATSCompliance } from '@soVendor/utils/atsComplianceAnalyzer';
import guestOptimizationStorage from '@soVendor/utils/guestOptimizationStorage';
import { getCurrentUserId } from '@soVendor/utils/authUtils';
// useResumeManagement is now handled by UploadStep component
import { useJobScraping } from '@soVendor/hooks/useJobScraping';
import { deriveInsightsFromResume } from '@soVendor/services/journeyProfileInsightsService';
import { saveUserResume } from '@soVendor/services/journeyService';
import {
  parseResumeSections,
  sortSectionsByCanonicalOrder,
  normalizeSectionTitles,
  arrangeResumeSections,
  bucketResumeSections,
} from './utils/parseResumeSections';
import type { ResumeSection } from './utils/parseResumeSections';
import LoadingSpinner from '@soVendor/components/LoadingSpinner';
import { appLogger } from '@soVendor/utils/appLogger';
import { normalizeResumeText } from '@soVendor/utils/pdfTextSanitizer';
import { generateResumePDF, generateResumeDOCX } from '@soVendor/utils/resumeDocumentGenerator';
import type { TemplateId } from '@soVendor/utils/resumeDocumentGenerator';
import { captureResumePreviewAsPdf } from './captureResumePreviewAsPdf';
import { getTemplateSkinClass as getSkinFromTemplateId } from './ResumePreviewForPDF';
import { reconstructStructuredResumeText } from '@soVendor/utils/promptBuilder';
import { parseSavedProfileResumeFile, serializeSavedProfileResumeFile } from './utils/savedProfileResumeFile';
import { useAuth } from '@core/auth/useAuth';
import { jobService } from '@core/supabase/services/jobService';
import { candidateService } from '@core/supabase/services/candidateService';
import { rolefitProjectionService } from '@core/supabase/services/rolefitProjectionService';
import { buildCandidateResumeText } from '@modules/role-fit/buildCandidateResumeText';
import { b2bJobToOptimizerJob } from '@modules/role-fit/b2bJobToOptimizerJob';
import { buildRolefitProjectionUpsertFromOptimization } from '@modules/role-fit/rolefitProjectionPayload';
import { getOptimizerFlowStepLabel } from '../optimizerFlowBreadcrumb';

import type { DashboardPage } from './DashboardContainerStub';

interface SingleOptimizeFlowProps {
  initialStepFromUrl?: string;
  /** B2B: deep link from application detail `?applicationId=` — syncs fitment/gap to `ggj_b2b_optimizations` + application (MAT-020). */
  initialApplicationId?: string | null;
  /** B2B RoleFit AI: prefill job + candidate profile; skip parsed-resume and job-URL steps. */
  roleFitPrefetch?: {
    candidateId: string;
    jobId: string;
    returnTo?: string;
    candidateName: string;
    candidateEmail?: string;
    /** Shown in shell header / upload context when launched from RoleFit AI */
    jobTitle?: string;
    clientName?: string;
  };
  /** After download in RoleFit flow, skip apply/save modals and return to RoleFit (or `returnTo`). */
  onRoleFitAfterDownload?: () => void;
  /** Called when flow is done. If user chose a next action from the modal, nextAction is set so parent can navigate there. */
  onComplete: (result: OptimizationResult, nextAction?: NextActionPage) => void;
  onBack: () => void;
  /** When on native and tab bar is hidden, steps use this to show "More" menu (Home, Optimizer, Activities, Me). */
  onNavigate?: (page: DashboardPage) => void;
  /** When true (guest flow), step progress and step indicator are hidden to reduce friction. */
  isGuest?: boolean;
  /** When provided (e.g. for guests), called when user taps "Sign up to download" on results. */
  onSignup?: () => void;
  /** B2B: updates the shell breadcrumb last segment (Upload, Match analysis, …). */
  onBreadcrumbStepChange?: (stepLabel: string) => void;
}

/** sessionStorage key for tab-switch / window-switch persistence of the in-progress optimization flow */
const FLOW_SESSION_KEY = 'opt_flow_autosave';

/**
 * Reads and parses the autosave snapshot from sessionStorage.
 * Defined outside the component so it is never called during render
 * (only from lazy useState initializers, which React calls exactly once).
 * Returns null if the snapshot is missing, stale (>2 h), or has no resumeData.
 */
function readAutosaveSnapshot(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(FLOW_SESSION_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as Record<string, unknown>;
    const savedAt = snap.savedAt as number | undefined;
    if (!savedAt || Date.now() - savedAt > 2 * 60 * 60 * 1000) return null;
    if (!snap.resumeData) return null;
    return snap;
  } catch {
    return null;
  }
}

/** Same pipeline as UploadStep `resumeDataForReview` for RoleFit CRM profile text (synthetic .txt). */
function buildRoleFitResumeDataFromProfileText(profileText: string, candidateLabel: string): ResumeData {
  const trimmed = profileText.trim();
  const baseName =
    candidateLabel
      .replace(/[^a-zA-Z0-9\s.-]+/g, '')
      .trim()
      .slice(0, 48)
      .replace(/\s+/g, '-') || 'candidate';
  const fileName = `${baseName}-profile.txt`;
  const file = new File([trimmed], fileName, { type: 'text/plain' });
  const rawSections = parseResumeSections(trimmed);
  const sectionList = bucketResumeSections(arrangeResumeSections(rawSections));
  return {
    id: 'temp-resume-id',
    fileName,
    fileType: 'txt',
    fileSize: file.size,
    parsedText: trimmed,
    sections: sectionList,
    metadata: {
      wordCount: trimmed.split(/\s+/).filter(Boolean).length,
      pageCount: 1,
      lastModified: new Date(),
    },
    uploadDate: new Date(),
  };
}

const isValidAppUuid = (s: string | null | undefined) =>
  !!s &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s));

const SingleOptimizeFlow: React.FC<SingleOptimizeFlowProps> = ({
  initialStepFromUrl,
  initialApplicationId,
  roleFitPrefetch,
  onRoleFitAfterDownload,
  onComplete,
  onBack,
  onNavigate,
  isGuest = false,
  onSignup: onSignupProp,
  onBreadcrumbStepChange,
}) => {
  const isRoleFitFlow = !!(
    roleFitPrefetch?.candidateId &&
    roleFitPrefetch?.jobId
  );
  // Map URL step to step index
  const getStepIndexFromUrl = (urlStep: string): number => {
    const stepMap: Record<string, number> = {
      'upload': 0,
      'template': 1,
      'parsed-resume': 2,
      'job-url': 3,
      'baseline': 4,
      'optimize': 5,
      'processing': 6,
      'results': 7,
      'download': 8,
      'save': 9,
      'apply': 10,
      'tracking': 11
    };
    return stepMap[urlStep] ?? 0;
  };

  // Deep-link URLs for processing/results steps (step ≥ 6) have no meaning on a fresh load —
  // the flow state lives only in-memory. Always start from Upload (0) for those URLs.
  // Lazy initializers (functions) are called by React exactly ONCE — never during re-renders —
  // so sessionStorage is not read on every render, preventing DOM reconciler conflicts.
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const fromRoleFit = !!(roleFitPrefetch?.candidateId && roleFitPrefetch?.jobId);
    if (fromRoleFit) {
      try {
        sessionStorage.removeItem(FLOW_SESSION_KEY);
        sessionStorage.removeItem('opt_upload_draft');
        guestOptimizationStorage.clear();
      } catch (_) { /* ignore */ }
    }
    // After signup/login from guest gate at step 4, land on Optimization settings (step 5)
    if (!fromRoleFit) {
      try {
        const afterStep = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('optimize_after_login_step') : null;
        if (afterStep !== null && afterStep !== '') {
          sessionStorage.removeItem('optimize_after_login_step');
          const step = parseInt(afterStep, 10);
          if (!Number.isNaN(step)) return step;
        }
      } catch (_) { /* ignore */ }
    }
    if (!fromRoleFit) {
      const snap = readAutosaveSnapshot();
      if (snap) {
        // Autosave takes priority over URL-derived step so tabs always resume correctly.
        if (snap.optimizationResult) return (snap.currentStep as number) ?? 0;
        return Math.min((snap.currentStep as number) ?? 0, 5);
      }
    }
    const idx = initialStepFromUrl ? getStepIndexFromUrl(initialStepFromUrl) : 0;
    return idx >= 6 ? 0 : idx;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flow data — lazy-initialised from autosave so the first render already has correct data
  // with no visible flash of "Resume required" or empty state.
  const [resumeData, setResumeData] = useState<ResumeData | null>(() =>
    isRoleFitFlow ? null : (readAutosaveSnapshot()?.resumeData as ResumeData) ?? null
  );
  const [jobData, setJobData] = useState<JobData | null>(() =>
    isRoleFitFlow ? null : (readAutosaveSnapshot()?.jobData as JobData) ?? null
  );
  const [baselineScores, setBaselineScores] = useState<BaselineScores | null>(() =>
    isRoleFitFlow ? null : (readAutosaveSnapshot()?.baselineScores as BaselineScores) ?? null
  );
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(() =>
    isRoleFitFlow ? null : (readAutosaveSnapshot()?.optimizationResult as OptimizationResult) ?? null
  );
  const [isOptimizing, setIsOptimizing] = useState(false);
  
  // Tracks the in-flight background-save promise so handleSave can await it before
  // deciding whether to create a new DB row (prevents duplicate optimization records).
  const bgSavePromiseRef = useRef<Promise<string | null>>(Promise.resolve(null));
  const bgSaveIdRef = useRef<string | null>(null);

  // Additional data for extended flow
  const [downloadedFormat, setDownloadedFormat] = useState<string | null>(null);
  const [savedVersionId, setSavedVersionId] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const applicationIdRef = useRef<string | null>(null);
  useEffect(() => {
    applicationIdRef.current = applicationId;
  }, [applicationId]);

  useEffect(() => {
    if (isValidAppUuid(initialApplicationId ?? undefined)) {
      setApplicationId(initialApplicationId!);
    }
  }, [initialApplicationId]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() =>
    isRoleFitFlow ? 'ats-optimized' : (readAutosaveSnapshot()?.selectedTemplateId as string) ?? 'ats-optimized'
  );
  const [selectedDesign, setSelectedDesign] = useState<{ font: string; color: string; caps: string }>(() =>
    isRoleFitFlow
      ? { font: 'default', color: '', caps: 'none' }
      : (readAutosaveSnapshot()?.selectedDesign as { font: string; color: string; caps: string }) ?? {
          font: 'default',
          color: '',
          caps: 'none',
        }
  );
  const [showApplyAfterDownloadModal, setShowApplyAfterDownloadModal] = useState(false);
  const [showWhereToGoAfterDownloadModal, setShowWhereToGoAfterDownloadModal] = useState(false);
  const [showNextActionModal, setShowNextActionModal] = useState(false);
  /** Guest flow: show sign-up modal when trying to go from analysis (step 4) to optimization settings (step 5) */
  const [showGuestSignUpModal, setShowGuestSignUpModal] = useState(false);
  /** After job match analysis (step 4): confirm before spending credits / running full optimization */
  const [showPostBaselineGenerateModal, setShowPostBaselineGenerateModal] = useState(false);
  /** Avoid duplicate uploads when optimizationResult updates */
  /** True while loading optimization + job from History "Apply" so we show loading instead of upload step */
  const [restoringFromHistoryApply, setRestoringFromHistoryApply] = useState(() => {
    if (isRoleFitFlow) return false;
    return typeof sessionStorage !== 'undefined' ? !!sessionStorage.getItem('history_apply_optimization_id') : false;
  });

  useEffect(() => {
    onBreadcrumbStepChange?.(getOptimizerFlowStepLabel(currentStep));
  }, [currentStep, onBreadcrumbStepChange]);

  const [roleFitProfileText, setRoleFitProfileText] = useState<string | null>(null);
  const [roleFitPrefetchLoading, setRoleFitPrefetchLoading] = useState(isRoleFitFlow);
  const [roleFitFetchError, setRoleFitFetchError] = useState<string | null>(null);
  const clearedRoleFitAutosaveRef = useRef(false);

  // RoleFit entry: avoid restoring an unrelated autosave into this guided path.
  useEffect(() => {
    if (!isRoleFitFlow || clearedRoleFitAutosaveRef.current) return;
    clearedRoleFitAutosaveRef.current = true;
    try {
      sessionStorage.removeItem(FLOW_SESSION_KEY);
      sessionStorage.removeItem('opt_upload_draft');
    } catch (_) { /* ignore */ }
  }, [isRoleFitFlow]);

  useEffect(() => {
    if (!isRoleFitFlow || !roleFitPrefetch?.candidateId || !roleFitPrefetch?.jobId) return;
    let cancelled = false;
    (async () => {
      setRoleFitPrefetchLoading(true);
      setRoleFitFetchError(null);
      try {
        const [job, candidate] = await Promise.all([
          jobService.getById(roleFitPrefetch.jobId),
          candidateService.getById(roleFitPrefetch.candidateId),
        ]);
        if (cancelled) return;
        if (!job) {
          setRoleFitFetchError('Job could not be loaded.');
          return;
        }
        if (!candidate) {
          setRoleFitFetchError('Candidate could not be loaded.');
          return;
        }
        const base = b2bJobToOptimizerJob(job);
        const jd: JobData = {
          id: job.id,
          title: base.title,
          company: base.company,
          description: base.description || '',
          location: base.location || undefined,
          url: job.jd_url || undefined,
          skills: base.skills ?? [],
          requirements: base.requirements ?? [],
        };
        setJobData(jd);
        const profileText = buildCandidateResumeText(candidate);
        setRoleFitProfileText(profileText);
        if (profileText.trim()) {
          const label =
            [roleFitPrefetch.candidateName, roleFitPrefetch.candidateEmail].filter(Boolean).join(' · ') ||
            roleFitPrefetch.candidateName ||
            'Candidate';
          setResumeData((prev) => {
            if (prev?.parsedText?.trim()) return prev;
            return buildRoleFitResumeDataFromProfileText(profileText, label);
          });
        }
      } catch (e) {
        if (!cancelled) {
          setRoleFitFetchError(e instanceof Error ? e.message : 'Failed to load RoleFit data.');
        }
      } finally {
        if (!cancelled) setRoleFitPrefetchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isRoleFitFlow, roleFitPrefetch?.candidateId, roleFitPrefetch?.jobId]);

  // Hooks
  const optimizationHook = useOptimizationProcess();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { hasEnoughCredits, deductCredits, addCredits, credits, fetchCredits } = useCreditSystem();

  /** MAT-020: persist B2B optimization + application fitment when `applicationId` is set (URL or Apply step). */
  const pushB2bOptimizationSync = useCallback(async (versionId: string | null, result: OptimizationResult | null) => {
    const appId = applicationIdRef.current;
    if (!appId || !result) return;
    try {
      const { optimizationService } = await import('@core/supabase/services/optimizationService');
      const vid = versionId && isValidAppUuid(versionId) ? versionId : null;
      const match =
        (result as { optimizedScore?: number; match_score?: number }).optimizedScore ??
        (result as { match_score?: number }).match_score;
      const initial =
        (result as { originalScore?: number; initial_score?: number }).originalScore ??
        (result as { initial_score?: number }).initial_score;
      await optimizationService.syncFromSingleOptimizeFlow(appId, {
        b2c_optimization_id: vid,
        match_score: match != null && !Number.isNaN(Number(match)) ? Number(match) : null,
        initial_score: initial != null && !Number.isNaN(Number(initial)) ? Number(initial) : null,
        fit_assessment: result.fit_assessment ?? null,
        skill_gaps:
          (result as { skillGaps?: unknown; skill_gaps?: unknown }).skillGaps ??
          (result as { skill_gaps?: unknown }).skill_gaps ??
          null,
      });
    } catch (e) {
      appLogger.warn('[SingleOptimizeFlow] B2B optimization sync skipped:', e);
    }
  }, []);

  // ── Autosave flow state to sessionStorage on every meaningful change ─────
  // This allows the flow to survive tab-switches and window-refocus events
  // where React re-mounts the component but the auth session is still valid.
  useEffect(() => {
    // Only save when we have at least some progress (past the initial upload step)
    if (currentStep === 0 && !resumeData) return;
    // Never save step 6 (ProcessingStep) — it's a transient in-flight state.
    // Saving it would cause the restore to jump back to step 5 mid-processing
    // or trigger a re-run of the LLM calls on session refresh.
    if (currentStep === 6) return;
    try {
      const snapshot = {
        currentStep,
        resumeData,
        jobData,
        baselineScores,
        optimizationResult,
        selectedTemplateId,
        selectedDesign,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(FLOW_SESSION_KEY, JSON.stringify(snapshot));
    } catch { /* ignore quota errors */ }
  }, [currentStep, resumeData, jobData, baselineScores, optimizationResult, selectedTemplateId, selectedDesign]);

  // Standalone Download step removed; PDF is on Results. Clamp stale autosave/URL step 8 → Results.
  useLayoutEffect(() => {
    if (currentStep === 8) setCurrentStep(7);
  }, [currentStep]);

  // ── Log autosave restoration once on mount ───────────────────────────────
  useEffect(() => {
    const snap = readAutosaveSnapshot();
    if (snap) {
      const step = snap.optimizationResult
        ? ((snap.currentStep as number) ?? 0)
        : Math.min((snap.currentStep as number) ?? 0, 5);
      appLogger.debug('[AUTOSAVE] Flow state initialised from sessionStorage', { step });
    }
  }, []);

  // ── Clear pending-resume marker on unmount (NOT the autosave snapshot) ──
  // We intentionally keep FLOW_SESSION_KEY alive across tab switches so that
  // coming back to the optimizer restores exactly where the user left off.
  // The autosave snapshot is only removed when the user explicitly starts a
  // new optimization or completes the flow (see onNewOptimization / NextActionModal).
  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem('optimize-pending-resume');
      } catch { /* ignore */ }
    };
  }, []);

  // Clear flow-level error when on Upload step so upload card doesn't show stale errors from other steps (e.g. optimization)
  React.useEffect(() => {
    if (currentStep === 0) setError(null);
  }, [currentStep]);

  // Signal the Layout to switch to compact icon-strip mode when on the resume editor step
  React.useEffect(() => {
    const active = currentStep === 2;
    window.dispatchEvent(new CustomEvent('ggj:editor-mode', { detail: { active } }));
    return () => {
      // Always deactivate when this component unmounts
      window.dispatchEvent(new CustomEvent('ggj:editor-mode', { detail: { active: false } }));
    };
  }, [currentStep]);

  // Resume management will be handled by UploadStep component
  const jobHook = useJobScraping();

  const stepTitleKeys: Record<string, string> = {
    'upload': 'optimizer.upload',
    'template': 'optimizer.chooseTemplateShort',
    'parsed-resume': 'optimizer.parsedResume',
    'job-url': 'optimizer.jobUrlInput',
    'baseline': 'optimizer.baselineShort',
    'optimize': 'optimizer.optimizeShort',
    'processing': 'optimizer.processing',
    'results': 'optimizer.resultsShort',
    'download': 'optimizer.downloadShort',
    'save': 'optimizer.saveShort',
    'apply': 'optimizer.applyShort',
    'tracking': 'optimizer.trackingShort',
  };
  const stepDescKeys: Record<string, string> = {
    'upload': 'optimizer.resumeFile',
    'template': 'optimizer.template',
    'parsed-resume': 'optimizer.reviewSections',
    'job-url': 'optimizer.jobPosting',
    'baseline': 'optimizer.jobMatchAnalysis',
    'optimize': 'optimizer.aiProcessing',
    'processing': 'optimizer.inProgress',
    'results': 'optimizer.optimizedResume',
    'download': 'optimizer.getResume',
    'save': 'optimizer.toHistory',
    'apply': 'optimizer.toJob',
    'tracking': 'optimizer.applicationStatus',
  };
  const steps = [
    { id: 'upload', title: `[${t(stepTitleKeys['upload'])}]`, description: t(stepDescKeys['upload']), completed: !!resumeData, active: currentStep === 0 },
    { id: 'template', title: `[${t(stepTitleKeys['template'])}]`, description: t(stepDescKeys['template']), completed: isRoleFitFlow ? currentStep > 0 : currentStep > 1, active: currentStep === 1 },
    { id: 'parsed-resume', title: `[${t(stepTitleKeys['parsed-resume'])}]`, description: t(stepDescKeys['parsed-resume']), completed: isRoleFitFlow ? !!resumeData : currentStep > 2, active: currentStep === 2 },
    { id: 'job-url', title: `[${t(stepTitleKeys['job-url'])}]`, description: t(stepDescKeys['job-url']), completed: isRoleFitFlow ? !!jobData : !!jobHook.jobDataArray[0]?.data || !!jobData, active: currentStep === 3 },
    { id: 'baseline', title: `[${t(stepTitleKeys['baseline'])}]`, description: t(stepDescKeys['baseline']), completed: !!baselineScores, active: currentStep === 4 },
    { id: 'optimize', title: `[${t(stepTitleKeys['optimize'])}]`, description: t(stepDescKeys['optimize']), completed: !!optimizationResult, active: currentStep === 5 },
    { id: 'processing', title: `[${t(stepTitleKeys['processing'])}]`, description: t(stepDescKeys['processing']), completed: !!optimizationResult, active: currentStep === 6 },
    { id: 'results', title: `[${t(stepTitleKeys['results'])}]`, description: t(stepDescKeys['results']), completed: !!optimizationResult, active: currentStep === 7 },
    { id: 'download', title: `[${t(stepTitleKeys['download'])}]`, description: t(stepDescKeys['download']), completed: !!downloadedFormat, active: false },
    { id: 'save', title: `[${t(stepTitleKeys['save'])}]`, description: t(stepDescKeys['save']), completed: !!savedVersionId, active: currentStep === 9 },
    { id: 'apply', title: `[${t(stepTitleKeys['apply'])}]`, description: t(stepDescKeys['apply']), completed: !!applicationId, active: currentStep === 10 },
    { id: 'tracking', title: `[${t(stepTitleKeys['tracking'])}]`, description: t(stepDescKeys['tracking']), completed: false, active: currentStep === 11 }
  ];

  // Restore guest optimization state after authentication (must not run for RoleFit — it would jump to e.g. download from localStorage)
  useEffect(() => {
    if (isRoleFitFlow) return;

    let mounted = true;
    let clearDelayId: ReturnType<typeof setTimeout> | null = null;
    
    const restoreGuestOptimization = async () => {
      appLogger.debug('[RESTORE] Checking for guest optimization data');

      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const hasPending = guestOptimizationStorage.hasPendingDownload();
      const savedData = guestOptimizationStorage.get();

      // Restore from storage immediately (sync) so the result card shows without waiting for auth
      if (mounted && savedData) {
        appLogger.debug('[RESTORE] Saved data found - restoring state', { hasSession: !!currentSession });
        appLogger.debug('[RESTORE] Saved data', {
          resumeDataExists: !!savedData.resumeData,
          jobDataExists: !!savedData.jobData,
          baselineScoresExists: !!savedData.baselineScores,
          optimizationResultExists: !!savedData.optimizationResult,
          savedStep: savedData.currentStep,
          currentUrlStep: currentStep
        });

        const restoredResumeData = savedData.resumeData ||
          (savedData.optimizationResult?.resumeId ? { id: savedData.optimizationResult.resumeId } : null);
        const restoredJobData = savedData.jobData ||
          (savedData.optimizationResult?.jobId ? { id: savedData.optimizationResult.jobId } : null);

        setResumeData(restoredResumeData);
        setJobData(restoredJobData);
        setBaselineScores(savedData.baselineScores);

        if (savedData.optimizationResult) {
          appLogger.debug('[RESTORE] Restoring optimization result');
          setOptimizationResult(savedData.optimizationResult);
        }

        if (savedData.currentStep !== undefined && savedData.currentStep !== currentStep) {
          appLogger.debug('[RESTORE] Jumping to saved step', savedData.currentStep);
          setCurrentStep(savedData.currentStep);
        }

        appLogger.debug('[RESTORE] Optimization state restored');

        if (hasPending) {
          guestOptimizationStorage.clearPendingDownload();
        }

        if (mounted && currentSession) {
          clearDelayId = window.setTimeout(() => {
            if (mounted) {
              appLogger.debug('[RESTORE] Clearing guest storage after sign-in');
              guestOptimizationStorage.clear();
            }
          }, 2000);
        }
      } else {
        if (mounted && currentSession) {
          appLogger.debug('[RESTORE] Session exists but no saved data');
        } else if (mounted) {
          appLogger.debug('[RESTORE] No saved data');
        }
      }
    };
    
    // Check immediately on mount
    appLogger.debug('[RESTORE] Component mounted - checking for restoration');
    restoreGuestOptimization();
    
    // Also listen for auth state changes (for when user logs in or tab is refocused)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      appLogger.debug('[RESTORE] Auth state changed:', event, 'Session:', !!session);
      
      if (mounted && event === 'SIGNED_IN' && session) {
        appLogger.debug('[RESTORE] SIGNED_IN event detected - checking for restoration');
        
        if (guestOptimizationStorage.hasPendingDownload()) {
          appLogger.debug('[RESTORE] Pending download detected - triggering restoration');
          setTimeout(() => restoreGuestOptimization(), 500);
        }
      }

    });
    
    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (clearDelayId != null) window.clearTimeout(clearDelayId);
    };
  }, [isRoleFitFlow]);

  // When user clicked "Apply" from History: load that optimization + job and go straight to Apply step (job apply flow)
  useEffect(() => {
    if (isRoleFitFlow) return;

    let mounted = true;
    const HISTORY_APPLY_OPT = 'history_apply_optimization_id';
    const HISTORY_APPLY_JOB_ID = 'history_apply_job_id';
    const HISTORY_APPLY_JOB_URL = 'history_apply_job_url';
    const HISTORY_APPLY_JOB_TITLE = 'history_apply_job_title';
    const HISTORY_APPLY_COMPANY = 'history_apply_company';

    const run = async () => {
      const optId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(HISTORY_APPLY_OPT) : null;
      if (!optId) {
        if (mounted) setRestoringFromHistoryApply(false);
        return;
      }

      const userId = await getCurrentUserId();
      if (!userId || !mounted) {
        if (mounted) setRestoringFromHistoryApply(false);
        return;
      }

      try {
        const [r1, r2] = await Promise.all([
          supabase.from('ggj_llm_optimization_level1').select('optimization_id, optimized_resume, final_resume, initial_score, match_score, improvements, resume_id, job_id, created_at').eq('optimization_id', optId).eq('user_id', userId).maybeSingle(),
          supabase.from('ggj_llm_optimization_level2').select('optimization_id, optimized_resume, final_resume, initial_score, match_score, improvements, resume_id, job_id, created_at').eq('optimization_id', optId).eq('user_id', userId).maybeSingle(),
        ]);
        const row = r1.data ?? r2.data;
        if (!row || !mounted) {
          if (mounted) setRestoringFromHistoryApply(false);
          return;
        }

        const optimizedText = (row as { optimized_resume?: string; final_resume?: string }).optimized_resume || (row as { final_resume?: string }).final_resume || '';
        const jobId = (sessionStorage.getItem(HISTORY_APPLY_JOB_ID) || (row as { job_id?: string }).job_id) ?? '';
        const jobTitle = sessionStorage.getItem(HISTORY_APPLY_JOB_TITLE) || '';
        const company = sessionStorage.getItem(HISTORY_APPLY_COMPANY) || '';
        const jobUrl = sessionStorage.getItem(HISTORY_APPLY_JOB_URL) || '';

        const job: JobData = {
          id: jobId,
          title: jobTitle || 'Job',
          company: company || 'Company',
          description: '',
          url: jobUrl || undefined,
        };

        const placeholderScores: BaselineScores = {
          formatting: (row as { initial_score?: number }).initial_score ?? 0,
          keywords: 0,
          structure: 0,
          overall: (row as { initial_score?: number }).initial_score ?? 0,
          atsRating: 'Good',
        };

        const result: OptimizationResult = {
          id: optId,
          resumeId: (row as { resume_id?: string }).resume_id,
          jobId: jobId || undefined,
          originalResume: '',
          optimizedResume: optimizedText,
          originalScore: (row as { initial_score?: number }).initial_score ?? 0,
          optimizedScore: (row as { match_score?: number }).match_score ?? 0,
          beforeScores: placeholderScores,
          afterScores: { ...placeholderScores, overall: (row as { match_score?: number }).match_score ?? 0 },
          improvements: Array.isArray((row as { improvements?: string[] }).improvements) ? (row as { improvements: string[] }).improvements : [],
          timestamp: (row as { created_at?: string }).created_at ? new Date((row as { created_at: string }).created_at) : new Date(),
        };

        sessionStorage.removeItem(HISTORY_APPLY_OPT);
        sessionStorage.removeItem(HISTORY_APPLY_JOB_ID);
        sessionStorage.removeItem(HISTORY_APPLY_JOB_URL);
        sessionStorage.removeItem(HISTORY_APPLY_JOB_TITLE);
        sessionStorage.removeItem(HISTORY_APPLY_COMPANY);
        try {
          sessionStorage.setItem('history_apply_flow_active', '1');
        } catch (_) { /* ignore */ }

        setJobData(job);
        setOptimizationResult(result);
        // Go directly to the Apply step (10), not the Save step (9)
        setCurrentStep(10);
        if (window.location.pathname !== '/optimize/apply') {
          window.history.replaceState({ step: 10, stepId: 'apply' }, '', '/optimize/apply');
        }
        if (mounted) setRestoringFromHistoryApply(false);
      } catch (e) {
        appLogger.error('[SingleOptimizeFlow] History apply restore failed', e);
        try {
          sessionStorage.removeItem(HISTORY_APPLY_OPT);
          sessionStorage.removeItem(HISTORY_APPLY_JOB_ID);
          sessionStorage.removeItem(HISTORY_APPLY_JOB_URL);
          sessionStorage.removeItem(HISTORY_APPLY_JOB_TITLE);
          sessionStorage.removeItem(HISTORY_APPLY_COMPANY);
        } catch (_) { /* ignore */ }
        if (mounted) setRestoringFromHistoryApply(false);
      }
    };

    run();
    return () => { mounted = false; };
  }, [isRoleFitFlow]);

  // Save optimization state for guests (before they authenticate)
  useEffect(() => {
    const saveOptimizationState = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Only save if user is NOT authenticated and has optimization result
      if (!session && optimizationResult && currentStep >= 5) {
        appLogger.debug('Saving guest optimization state');
        guestOptimizationStorage.save({
          currentStep,
          resumeData,
          jobData,
          baselineScores,
          optimizationResult,
        });
      }
    };
    
    saveOptimizationState();
  }, [currentStep, resumeData, jobData, baselineScores, optimizationResult]);

  // Get step details for URL and title
  const getStepDetails = (stepIndex: number) => {
    const step = steps[stepIndex];
    const titleKey = step ? stepTitleKeys[step.id] || 'optimizer.uploadResume' : 'optimizer.uploadResume';
    const descKey = step ? stepDescKeys[step.id] || 'optimizer.resumeFile' : 'optimizer.resumeFile';
    return {
      id: step?.id || 'upload',
      title: t(titleKey),
      description: t(descKey),
      progress: `${stepIndex + 1} of ${steps.length}`
    };
  };

  // Update URL and page title based on current step
  useEffect(() => {
    const stepDetails = getStepDetails(currentStep);
    const baseUrl = '/optimize';
    const newUrl = `${baseUrl}/${stepDetails.id}`;
    
    appLogger.debug('[URL UPDATE] Updating URL to:', newUrl, 'Step:', currentStep);
    
    // Update browser URL without triggering navigation
    if (window.location.pathname !== newUrl) {
      window.history.pushState({ step: currentStep, stepId: stepDetails.id }, '', newUrl);
    }
    
    // Update page title – no step count in tab title (avoids "Upload (1 of 11)" when user navigates away)
    document.title = `${stepDetails.title} - GoGrabJob`;
  }, [currentStep, steps]);

  // Restore resume data when landing on Job URL (step 2) with pending resume from sessionStorage
  useEffect(() => {
    if (currentStep !== 2) return;
    if (resumeData) {
      try { sessionStorage.removeItem('optimize-pending-resume'); } catch (_) { /* ignore */ }
      return;
    }
    try {
      const raw = sessionStorage.getItem('optimize-pending-resume');
      if (!raw) return;
      const parsed = JSON.parse(raw) as ResumeData & { metadata?: { lastModified?: string } };
      if (!parsed?.parsedText) return;
      const restored: ResumeData = {
        ...parsed,
        metadata: {
          ...parsed.metadata,
          lastModified: parsed.metadata?.lastModified ? new Date(parsed.metadata.lastModified) : new Date()
        }
      };
      sessionStorage.removeItem('optimize-pending-resume');
      setResumeData(restored);
    } catch (_) { /* ignore */ }
  }, [currentStep, resumeData]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const stepMatch = path.match(/\/optimize\/(.+)/);
      if (stepMatch) {
        const stepId = stepMatch[1];
        const stepIndex = steps.findIndex(step => step.id === stepId);
        appLogger.debug('[BROWSER NAV] Back/forward', stepId, stepIndex);
        if (stepIndex !== -1 && stepIndex !== currentStep) {
          setCurrentStep(stepIndex);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, steps]);

  // Simplified file upload handler - let UploadStep handle the actual upload
  const handleFileUpload = useCallback(async (file: File): Promise<ResumeData> => {
    appLogger.debug('[SingleOptimizeFlow] File upload', file.name);
    // UploadStep will handle the actual upload using its own resumeHook
    // We just return an empty ResumeData object since UploadStep will use resumeHook directly
    return {
      id: crypto.randomUUID(),
      fileName: file.name,
      fileType: file.type.split('/')[1] as 'pdf' | 'docx' | 'txt',
      fileSize: file.size,
      parsedText: '',
      sections: {},
      metadata: {
        wordCount: 0,
        pageCount: 1,
        lastModified: new Date()
      },
      uploadDate: new Date()
    };
  }, []);

  const handleJobParse = useCallback(async (urlOrText: string): Promise<JobData> => {
    const trimmed = urlOrText.trim();
    appLogger.debug('[handleJobParse]', trimmed.startsWith('http') ? 'URL' : 'pasted description');
    setIsLoading(true);
    setError(null);

    try {
      if (!trimmed) {
        throw new Error(t('optimizer.flow.validationJobUrlRequired'));
      }

      // Pasted job description (not a URL): create JobData from text (use valid UUID for DB)
      const isPastedDescription = !/^https?:\/\//i.test(trimmed);
      if (isPastedDescription) {
        const jobData: JobData = {
          id: crypto.randomUUID(),
          title: 'Job from description',
          company: 'Not specified',
          description: trimmed,
          url: undefined,
          required_skills: [],
          preferred_skills: [],
          requirements: [],
          skills: [],
          keywords: [],
          parsedDate: new Date()
        };
        setJobData(jobData);
        setIsLoading(false);
        return jobData;
      }

      // URL: use job-scraper
      const trimmedUrl = trimmed;
      appLogger.debug('[JobScraping] Using edge function');

      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/job-scraper`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ jobUrl: trimmedUrl })
      });

      if (!response.ok) {
        if (response.status === 401 && !session) {
          throw new Error('Job URL scraping requires authentication. Please paste the job description in the text area instead, or sign up to use URL scraping.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to scrape job: ${response.status}`);
      }

      const result = await response.json();
      const scrapedData = result.data || {};

      const jobData: JobData = {
        id: result.jobId || Date.now().toString(),
        url: trimmedUrl,
        title: scrapedData.title || t('optimizer.flow.unknownPosition'),
        company: scrapedData.company || t('optimizer.flow.unknownCompany'),
        location: scrapedData.location || 'Not specified',
        description: scrapedData.description || '',
        required_skills: scrapedData.skills || scrapedData.required_skills || [],
        preferred_skills: scrapedData.preferred_skills || [],
        experience_level: scrapedData.experience_level || scrapedData.experienceLevel || t('optimizer.flow.notSpecified'),
        posted_date: scrapedData.posted_date || scrapedData.postedDate,
        portal: scrapedData.portal || 'Unknown'
      };

      setJobData(jobData);
      return jobData;
    } catch (err) {
      appLogger.error('[JobScraping] Error', err);
      setError(err instanceof Error ? err.message : t('optimizer.flow.jobParsingFailed'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const handleBaselineAnalysis = useCallback(async (): Promise<BaselineScores> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Calculate real ATS scores for baseline analysis
      if (!resumeData || !jobData) {
        throw new Error('Missing resume or job data for analysis');
      }

      // Calculate score using simple algorithm
      const calculateJobMatchScore = (resumeText: string, skills: string[], description: string) => {
        const resumeLower = resumeText.toLowerCase();
        const descLower = description.toLowerCase();
        
        // Calculate keyword matches
        const skillMatches = skills.filter(skill => 
          resumeLower.includes(skill.toLowerCase())
        ).length;
        
        const skillScore = skills.length > 0 ? (skillMatches / skills.length) * 100 : 50;
        
        // Simple content match
        const words = descLower.split(/\s+/).filter(w => w.length > 4);
        const matchedWords = words.filter(w => resumeLower.includes(w)).length;
        const contentScore = words.length > 0 ? (matchedWords / words.length) * 100 : 50;
        
        // Average score
        return Math.round((skillScore + contentScore) / 2);
      };
      
      // Calculate initial job match score (semantic matching)
      const initialScore = calculateJobMatchScore(
        resumeData.parsedText,
        jobData.skills || [],
        jobData.description || ''
      );
      
      // Use proper ATS compliance analyzer for accurate technical scores
      const atsAnalysis = analyzeATSCompliance(resumeData.parsedText, {
        skills: jobData.skills || jobData.required_skills || [],
        description: jobData.description || '',
        requirements: jobData.requirements || []
      });
      
      // Calculate match quality factor (0.5 to 1.0)
      // If semantic match is poor, reduce all scores proportionally
      const matchFactor = Math.max(0.5, Math.min(1.0, initialScore / 100));
      
      // Apply match factor to technical scores
      // Poor job match = lower overall scores even if formatting is good
      const baselineScores: BaselineScores = {
        formatting: Math.round(atsAnalysis.sections.formatting * matchFactor),
        keywords: Math.round(atsAnalysis.sections.keywords * matchFactor),
        structure: Math.round(atsAnalysis.sections.structure * matchFactor),
        overall: Math.round(atsAnalysis.overallScore * matchFactor),
        atsRating: (atsAnalysis.overallScore * matchFactor) >= 85 ? 'Excellent' : (atsAnalysis.overallScore * matchFactor) >= 70 ? 'Good' : (atsAnalysis.overallScore * matchFactor) >= 50 ? 'Fair' : 'Poor'
      };
      
      appLogger.debug('[BaselineAnalysis] ATS Analysis', {
        jobMatchScore: initialScore,
        matchFactor: matchFactor,
        rawATSScores: {
          formatting: atsAnalysis.sections.formatting,
          keywords: atsAnalysis.sections.keywords,
          structure: atsAnalysis.sections.structure,
          overall: atsAnalysis.overallScore
        },
        adjustedBaselineScores: baselineScores,
        atsAnalysis: {
          strengths: atsAnalysis.strengths,
          recommendations: atsAnalysis.recommendations.slice(0, 3),
          warnings: atsAnalysis.warnings.slice(0, 3)
        },
        resumeLength: resumeData.parsedText.length,
        jobSkills: jobData.skills?.length || 0
      });
      
      setBaselineScores(baselineScores);
      // Don't auto-advance - let user review the job match analysis
      // setCurrentStep(3);  // Removed: User should manually click Next to proceed
      return baselineScores;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Job match analysis failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [resumeData, jobData]);

  const handleOptimize = useCallback(async (model: string, preferences: any) => {
    // Prevent duplicate calls
    if (isOptimizing) {
      appLogger.debug('[Optimization] Already in progress, skipping');
      return;
    }

    setIsOptimizing(true);
    setError(null);
    
    try {
      let effectiveResume: ResumeData | null = resumeData;
      if (!effectiveResume?.parsedText?.trim() && isRoleFitFlow && roleFitProfileText?.trim() && roleFitPrefetch) {
        const label =
          [roleFitPrefetch.candidateName, roleFitPrefetch.candidateEmail].filter(Boolean).join(' · ') ||
          roleFitPrefetch.candidateName ||
          'Candidate';
        effectiveResume = buildRoleFitResumeDataFromProfileText(roleFitProfileText, label);
        setResumeData(effectiveResume);
      }
      if (!effectiveResume?.parsedText?.trim()) {
        throw new Error('Resume is required to optimize. Please go back and upload a resume.');
      }
      if (!jobData) {
        throw new Error('Job details are required to optimize. Please go back and enter a job URL.');
      }

      // Every optimization costs exactly 1 credit, regardless of AI model.
      // Model choice affects quality only — not credit pricing.
      const modelCredits = 1;

      // GUEST USER FIX: Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      const isGuest = !session;
      
      if (isGuest) {
        // Guest users may not deduct credits from DB; allow Groq only
        if (!model.includes('groq')) {
          throw new Error('Please sign up to use premium AI models.');
        }
        appLogger.debug('[Optimization] Guest user - Groq model allowed');
      } else {
        // Ensure credits are loaded before checking — re-fetch if still null
        if (credits === null) {
          await fetchCredits();
        }
        // Authenticated: always check 1 credit regardless of model
        if (!hasEnoughCredits(modelCredits)) {
          throw new Error('You have no credits remaining. Please upgrade your plan to continue optimizing.');
        }
        appLogger.debug('[Optimization] Deducting 1 credit for optimization');
        const deducted = await deductCredits(modelCredits);
        if (!deducted) {
          throw new Error('Failed to deduct credit. Please try again.');
        }
        appLogger.debug('[Optimization] Credit deducted successfully');
      }

      // Clear any previous result so step 6 renders ProcessingStep and runs AI (no skip to results)
      setOptimizationResult(null);
      // Advance to ProcessingStep (step 6) so user sees live optimization
      setCurrentStep(6);
      
      // Convert JobData to optimization types format (jobId must be a valid UUID for DB)
      const rawId = jobData.id ?? (jobData as { jobId?: string }).jobId;
      const jobIdForApi = typeof rawId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(rawId)
        ? rawId
        : crypto.randomUUID();
      const optimizationJobData = {
        title: jobData.title,
        company: jobData.company,
        location: jobData.location || 'Remote',
        responsibilities: jobData.requirements || [],
        requirements: jobData.requirements || [],
        skills: jobData.skills || [],
        description: jobData.description,
        jobUrl: jobData.url,
        jobId: jobIdForApi
      };

      // Per-section optimization now runs inside ProcessingStep directly.
      // handleOptimize's job here is only to gate credits and navigate to step 6.
      appLogger.debug('[Optimization] Navigating to ProcessingStep for per-section LLM optimization');
    } catch (err) {
      appLogger.error('Optimization failed', err);
      setError(err instanceof Error ? err.message : 'Optimization failed');
    } finally {
      setIsOptimizing(false);
    }
  }, [
    resumeData,
    jobData,
    baselineScores,
    hasEnoughCredits,
    deductCredits,
    addCredits,
    credits,
    fetchCredits,
    isRoleFitFlow,
    roleFitProfileText,
    roleFitPrefetch?.candidateName,
    roleFitPrefetch?.candidateEmail,
  ]);

  const handleDownload = useCallback(async (format: 'pdf' | 'docx' | 'txt'): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      if (!optimizationResult) {
        throw new Error('No optimization result available');
      }

      const optimizedText =
        optimizationResult.optimizedResume ||
        optimizationResult.final_resume ||
        optimizationResult.optimized_resume ||
        optimizationResult.optimizedText ||
        '';

      const normalizedText = normalizeResumeText(optimizedText);
      const fileName = `Resume_${(jobData?.title || 'Position').replace(/[^a-zA-Z0-9]/g, '_')}.${format}`;
      // Do not inject the target job title into the resume header —
      // the candidate's own headline comes from their resume content, not the job they're applying for.
      const jTitle = '';

      // Use LLM per-section results directly (already clean & sorted).
      // Do NOT run bucketResumeSections — that function aggressively reclassifies
      // every line and scrambles already-correct LLM output (same fix as ResultsStep).
      const rawLlmSections = (optimizationResult as any).sections as Array<{ title: string; content: string }> | undefined;
      const docSections: ResumeSection[] | undefined =
        Array.isArray(rawLlmSections) && rawLlmSections.length > 0
          ? sortSectionsByCanonicalOrder(
              normalizeSectionTitles(
                rawLlmSections
                  .filter(s => s?.title && s?.content?.trim())
                  .map(s => ({ title: s.title.trim(), content: s.content.trim(), preview: s.content.trim().slice(0, 120) }))
              )
            )
          : undefined;

      // Resolve the template:
      //   captureResumePreviewAsPdf expects the TEMPLATE ID (e.g. 'bold-dark') — it converts internally
      //   generateResumePDF / generateResumeDOCX expect the SKIN CLASS (e.g. 'black-bold')
      const tplId = selectedTemplateId || 'ats-optimized';
      const skinId = getSkinFromTemplateId(tplId) as TemplateId;

      let blob: Blob;

      if (format === 'txt') {
        const utf8Bom = '\uFEFF';
        blob = new Blob([utf8Bom + normalizedText], { type: 'text/plain;charset=utf-8' });
      } else if (format === 'pdf') {
        appLogger.debug('[Download] Generating PDF (preview-matched), template:', tplId, '→ skin:', skinId);
        if (docSections && docSections.length > 0) {
          blob = await captureResumePreviewAsPdf(docSections, tplId);
        } else {
          blob = await generateResumePDF(normalizedText, docSections, jTitle, skinId);
        }
      } else {
        appLogger.debug('[Download] Generating DOCX, template:', tplId, '→ skin:', skinId);
        blob = await generateResumeDOCX(normalizedText, docSections, jTitle, skinId);
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloadedFormat(format);
    } catch (err) {
      appLogger.error('Download failed', err);
      setError(err instanceof Error ? err.message : 'Download failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [optimizationResult, selectedTemplateId, jobData]);

  const handleDownloadPdfFromResults = useCallback(async () => {
    try {
      await handleDownload('pdf');
      if (!isRoleFitFlow) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) setShowApplyAfterDownloadModal(true);
      }
    } catch {
      /* error state set by handleDownload */
    }
  }, [handleDownload, isRoleFitFlow]);

  const handleRoleFitSaveToProfile = useCallback(async (fileContentFromUi: string) => {
    if (!isRoleFitFlow || !roleFitPrefetch?.candidateId || !optimizationResult) return;
    const rawInput = (fileContentFromUi || '').trim();
    const parsedFromUi = parseSavedProfileResumeFile(rawInput);
    const normalizedParsedBody = normalizeResumeText(parsedFromUi.body).trim();
    let bodyForSave = normalizedParsedBody;
    if (!bodyForSave) {
      const rawLlmSections = (optimizationResult as { sections?: Array<{ title: string; content: string }> }).sections;
      const fromSections =
        Array.isArray(rawLlmSections) && rawLlmSections.length > 0
          ? reconstructStructuredResumeText(rawLlmSections).trim()
          : '';
      const flat =
        optimizationResult.optimizedResume ||
        (optimizationResult as { optimizedText?: string }).optimizedText ||
        (optimizationResult as { optimized_resume?: string }).optimized_resume ||
        (optimizationResult as { final_resume?: string }).final_resume ||
        '';
      bodyForSave = normalizeResumeText(fromSections || flat || '').trim();
    }
    if (!bodyForSave) {
      setError('No optimized resume text to save.');
      return;
    }
    const reuseEnvelope =
      rawInput.includes('<<<GGJ_RESUME_BODY>>>') && normalizedParsedBody === bodyForSave;
    const filePayload = reuseEnvelope
      ? rawInput
      : serializeSavedProfileResumeFile(
          { templateId: selectedTemplateId, design: selectedDesign },
          bodyForSave
        );
    const candidateId = roleFitPrefetch.candidateId;
    setIsLoading(true);
    setError(null);
    try {
      const c = await candidateService.getById(candidateId);
      if (!c?.org_id) throw new Error('Candidate organization not found');
      const safeName = (c.full_name || 'candidate').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 80);
      const file = new File([filePayload], `${safeName}_optimized_resume.txt`, {
        type: 'text/plain;charset=utf-8',
      });
      const path = await candidateService.uploadResume(c.org_id, candidateId, file);
      await candidateService.update(candidateId, { base_resume_url: path });
      appLogger.debug('[SingleOptimizeFlow] RoleFit: optimized resume saved to candidate', { candidateId });
      try {
        sessionStorage.removeItem(FLOW_SESSION_KEY);
      } catch {
        /* ignore */
      }
      onRoleFitAfterDownload?.();
    } catch (e) {
      appLogger.warn('[SingleOptimizeFlow] RoleFit: save to profile failed', e);
      setError(e instanceof Error ? e.message : 'Could not save resume to profile');
    } finally {
      setIsLoading(false);
    }
  }, [
    isRoleFitFlow,
    roleFitPrefetch?.candidateId,
    optimizationResult,
    onRoleFitAfterDownload,
    selectedTemplateId,
    selectedDesign,
  ]);

  const handleSave = useCallback(async (saveData: {
    versionName: string;
    tags: string[];
    retentionPolicy: '30days' | 'indefinite';
    description?: string;
  }): Promise<string> => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!optimizationResult?.id) {
        throw new Error('No optimization result to save');
      }

      // Import the save function
      const { saveComprehensiveOptimization } = await import('@soVendor/services/optimizationService');
      
      appLogger.debug('[SaveHistory] Saving', saveData);
      
      // Check if this is a guest optimization (not saved to database)
      if (optimizationResult.id && optimizationResult.id.startsWith('guest-')) {
        appLogger.debug('[SaveHistory] Guest - cannot save to history');
        throw new Error('Guest optimizations cannot be saved to history. Please download your resume and run a fresh optimization as an authenticated user.');
      }
      
      const isValidUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s));

      // Wait for any in-flight background save to complete (race-condition guard).
      // This ensures handleSave never inserts a duplicate row when the background
      // save is still resolving at the time the user clicks "Save & Continue".
      const bgId = await bgSavePromiseRef.current.catch(() => null) ?? bgSaveIdRef.current;

      // Use the background-save UUID if available, OR the one already on the result.
      const existingDbId = (bgId && isValidUuid(bgId)) ? bgId
        : (optimizationResult.optimization_id && isValidUuid(optimizationResult.optimization_id))
          ? optimizationResult.optimization_id
          : null;

      if (existingDbId) {
        appLogger.debug('[SaveHistory] Background save already persisted this optimization — reusing:', existingDbId);
        setSavedVersionId(existingDbId);
        // Ensure result.id == real UUID so ApplyStep can set resume_version_id correctly
        setOptimizationResult(prev =>
          prev && prev.id !== existingDbId ? { ...prev, id: existingDbId, optimization_id: existingDbId } : prev
        );
        // Patch the background-saved record to include the template the user selected,
        // so OptimizationHistory can download with the correct template styling.
        if (selectedTemplateId) {
          supabase
            .from('ggj_llm_optimization_level2')
            .select('ai_insights')
            .eq('optimization_id', existingDbId)
            .maybeSingle()
            .then(({ data }) => {
              const merged = { ...(data?.ai_insights ?? {}), selectedTemplateId, selectedDesign };
              return supabase
                .from('ggj_llm_optimization_level2')
                .update({ ai_insights: merged })
                .eq('optimization_id', existingDbId);
            })
            .catch(() => {});
        }
        await pushB2bOptimizationSync(existingDbId, optimizationResult);
        return existingDbId;
      }

      let resumeIdToSave = (optimizationResult.resumeId && isValidUuid(optimizationResult.resumeId))
        ? optimizationResult.resumeId
        : resumeData?.id;
      let jobIdToSave = (optimizationResult.jobId && isValidUuid(optimizationResult.jobId))
        ? optimizationResult.jobId
        : jobData?.id;

      if (!resumeIdToSave || !jobIdToSave) {
        appLogger.error('[SaveHistory] Missing IDs', {
          resumeIdToSave,
          jobIdToSave,
          optimizationResultResumeId: optimizationResult.resumeId,
          optimizationResultJobId: optimizationResult.jobId
        });
        throw new Error('Missing resume or job ID for saving. Please run a fresh optimization as an authenticated user.');
      }
      if (!isValidUuid(resumeIdToSave)) resumeIdToSave = crypto.randomUUID();
      if (!isValidUuid(jobIdToSave)) jobIdToSave = crypto.randomUUID();

      // Inject selectedTemplateId, design settings, and sections into ai_insights so
      // My Activities download matches Download Step exactly.
      const resultWithTemplate = {
        ...optimizationResult,
        ai_insights: {
          ...(optimizationResult.ai_insights ?? {}),
          ...(selectedTemplateId && { selectedTemplateId }),
          ...(selectedDesign && { selectedDesign }),
          ...(Array.isArray((optimizationResult as any).sections) && { sections: (optimizationResult as any).sections }),
        },
      };

      const savedResult = await saveComprehensiveOptimization(
        resumeIdToSave,
        jobIdToSave,
        resultWithTemplate
      );
      
      const versionId = savedResult.optimization_id;

      appLogger.debug('[SaveHistory] Version saved', versionId);
      setSavedVersionId(versionId);
      // Patch result.id → real UUID so ApplyStep (step 10) can set resume_version_id correctly
      if (versionId) {
        setOptimizationResult(prev =>
          prev ? { ...prev, id: versionId, optimization_id: versionId } : prev
        );
      }

      await pushB2bOptimizationSync(versionId ?? null, resultWithTemplate as OptimizationResult);

      const optimizedText = optimizationResult.optimizedResume || (optimizationResult as { optimizedText?: string }).optimizedText || optimizationResult.originalResume;
      if (optimizedText) {
        deriveInsightsFromResume({
          rawText: optimizedText,
          sections: parseResumeSections(optimizedText),
        }).catch(() => {});
      }

      // Don't auto-advance, let user click Continue
      return versionId;
    } catch (err) {
      appLogger.error('[SaveHistory] Save failed', err);
      setError(err instanceof Error ? err.message : 'Save failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [optimizationResult, selectedTemplateId, selectedDesign, pushB2bOptimizationSync]);

  const handleApply = useCallback(async (applicationData: {
    applicationSnippet: string;
    coverLetterSnippet: string;
    notes?: string;
  }): Promise<string> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // The actual saving is handled in ApplyStep component
      // This is just for compatibility
      const appId = `app_${Date.now()}`;
      // Don't auto-advance, let user click Continue
      return appId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Application failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-trigger baseline analysis when reaching step 3 (baseline)
  React.useEffect(() => {
    if (currentStep === 4 && !baselineScores && resumeData && jobData && !isLoading) {
      appLogger.debug('[SingleOptimizeFlow] Auto-triggering baseline');
      handleBaselineAnalysis();
    }
  }, [currentStep, baselineScores, resumeData, jobData, isLoading, handleBaselineAnalysis]);

  const handleStepNext = useCallback(async (data?: any, templateId?: string) => {
    // Handle resume data from UploadStep (step 0): three-column Upload | Template | Parsed; optional templateId
    if (currentStep === 0 && data) {
      try {
        sessionStorage.setItem('optimize-pending-resume', JSON.stringify({
          ...data,
          metadata: data.metadata ? {
            ...data.metadata,
            lastModified: data.metadata.lastModified?.toISOString?.() ?? new Date().toISOString()
          } : undefined
        }));
      } catch (_) { /* ignore */ }
      setResumeData(data);
      if (templateId && typeof templateId === 'string') {
        setSelectedTemplateId(templateId);
      }
      const text = data.parsedText || data.content || '';
      if (text) {
        deriveInsightsFromResume({
          rawText: text,
          sections: parseResumeSections(text),
          structured: data.structured ?? undefined,
        }).catch(() => {});
        saveUserResume({
          file_name: data.fileName || 'resume',
          file_type: data.fileType,
          parsed_text: text,
          sections: data.sections ?? undefined,
          structured: data.structured ?? undefined,
        }).catch(() => {});
      }
      queueMicrotask(() => setCurrentStep(isRoleFitFlow ? 4 : 2));
      return;
    }

    // Handle completion of Review Sections (step 2): optional updated resumeData with sections stored
    if (currentStep === 2 && data) {
      setResumeData(data);
    }
    if (currentStep === 2) {
      queueMicrotask(() => setCurrentStep(3));
      return;
    }

    // Handle job data from JobUrlStep (step 3)
    if (currentStep === 3 && data) {
      setJobData(data);
    }

    // Handle application ID from ApplyStep (step 10)
    if (currentStep === 10 && data) {
      appLogger.debug('[SingleOptimizeFlow] Application ID', data);
      setApplicationId(data);
    }

    // From Application Tracking (step 11), "What's next?" → show next-action modal
    if (currentStep === 11) {
      setShowNextActionModal(true);
      return;
    }

    // ===== GUEST GATE: After analysis (step 4), before optimization settings (step 5) — show sign-up then redirect to step 5 =====
    if (isGuest && currentStep === 4) {
      setShowGuestSignUpModal(true);
      return;
    }

    if (currentStep === 4 && !baselineScores) {
      handleBaselineAnalysis();
      return;
    }

    // Authenticated users: confirm before leaving match analysis for full AI optimization
    if (currentStep === 4 && baselineScores) {
      setShowPostBaselineGenerateModal(true);
      return;
    }

    if (currentStep < steps.length - 1) {
      if (currentStep === 10) {
        setRestoringFromHistoryApply(false);
        try {
          sessionStorage.removeItem('history_apply_flow_active');
        } catch (_) { /* ignore */ }
      }
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, steps.length, baselineScores, handleBaselineAnalysis, optimizationResult, onComplete, isGuest]);

  const handleStepBack = useCallback(() => {
    if (currentStep === 10) {
      return;
    }
    if (currentStep > 0) {
      let prevStep =
        isRoleFitFlow && currentStep === 4
          ? 0
          : currentStep === 3
            ? 2
            : currentStep === 2
              ? 0
              : currentStep === 1
                ? 0
                : currentStep - 1;
      // Skip step 6 (ProcessingStep) when going backwards if optimization is already done —
      // we never want to re-run the AI when the user just navigates back from results.
      if (prevStep === 6 && optimizationResult) {
        prevStep = 5;
      }
      setCurrentStep(prevStep);
    } else {
      onBack();
    }
  }, [currentStep, onBack, optimizationResult, isRoleFitFlow]);

  // ── No-credits gate ─────────────────────────────────────────────────────────
  // Show a clear upgrade prompt before the user starts the full 5-step flow.
  // Check: credits === 0 (not null, which means loading) and they are not still
  // on the results screen (step ≥ 6).
  const isOutOfCredits = credits !== null && credits <= 0 && currentStep < 6;

  const renderCurrentStep = () => {
    // Gate: block the flow entry point when user has no credits
    if (isOutOfCredits && currentStep === 0) {
      return (
        <div className={STEP_MOTION_CLASS}>
          <div className={STEP_WRAPPER_CLASS}>
            <div className={`${STEP_CARD_CLASS} flex flex-col items-center justify-center gap-5 py-14 px-6 text-center`}>
              <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Zap className="w-7 h-7 text-red-500" />
              </div>
              <div>
                <h3 className="vscode-text-primary mb-1 text-lg font-bold">You're out of credits</h3>
                <p className="vscode-text-secondary text-sm max-w-xs">
                  You've used all your free optimizations. Upgrade to a Day Pass, Weekly, or Monthly plan to keep optimizing.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
                <button
                  type="button"
                  onClick={() => {
                    // Navigate to pricing page — Layout exposes onNavigate via prop chain
                    // We use a custom event the Layout listens for
                    window.dispatchEvent(new CustomEvent('navigate-to-pricing'));
                  }}
                  className="flex-1 flex items-center justify-center gap-2 min-h-[44px] px-5 py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#6C47FF] to-[#5B3FFF] hover:opacity-90 transition-opacity shadow-md touch-manipulation"
                >
                  <Zap className="w-4 h-4" />
                  [Upgrade Plan]
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="vscode-text-secondary flex flex-1 min-h-[44px] items-center justify-center gap-2 rounded-xl border border-solid px-5 py-2.5 text-sm font-semibold transition-colors hover:opacity-90 touch-manipulation vscode-border"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 0:
        return (
          <UploadStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            resumeData={resumeData || undefined}
            selectedTemplateId={selectedTemplateId}
            onFileUpload={handleFileUpload}
            isLoading={isLoading}
            error={error}
            roleFitPrefetchLoading={isRoleFitFlow && roleFitPrefetchLoading}
            roleFitFetchError={isRoleFitFlow ? roleFitFetchError : null}
            roleFitContext={
              isRoleFitFlow && jobData && roleFitProfileText != null && roleFitPrefetch
                ? {
                    jobTitle: jobData.title,
                    company: jobData.company,
                    description: jobData.description || '',
                    profileResumeText: roleFitProfileText,
                    candidateLabel: [roleFitPrefetch.candidateName, roleFitPrefetch.candidateEmail].filter(Boolean).join(' · '),
                    candidateName: roleFitPrefetch.candidateName,
                    candidateEmail: roleFitPrefetch.candidateEmail,
                    candidateId: roleFitPrefetch.candidateId,
                    jobId: roleFitPrefetch.jobId,
                    fromRoleFitAi: !!roleFitPrefetch.returnTo?.startsWith('/role-fit-ai'),
                  }
                : undefined
            }
          />
        );
      
      case 1:
        if (!resumeData) {
          return (
            <div className={STEP_MOTION_CLASS}>
              <div className={STEP_WRAPPER_CLASS}>
                <div className={`${STEP_CARD_CLASS} items-center justify-center p-8`}>
                  <p className="vscode-text-primary mb-4">Resume required to choose template.</p>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(0)}
                    className="vscode-text-secondary hover:vscode-text-primary flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
                  >
                    <span>←</span>
                    Back to Resume
                  </button>
                </div>
              </div>
            </div>
          );
        }
        return (
          <TemplateStep
            selectedTemplateId={selectedTemplateId}
            onNext={(templateId) => handleStepNext(templateId)}
            onBack={handleStepBack}
          />
        );

      case 2:
        if (!resumeData) {
          return (
            <div className={STEP_MOTION_CLASS}>
              <div className={STEP_WRAPPER_CLASS}>
                <div className={`${STEP_CARD_CLASS} items-center justify-center p-8`}>
                  <p className="vscode-text-primary mb-4">Resume required to review sections.</p>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(0)}
                    className="vscode-text-secondary hover:vscode-text-primary flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
                  >
                    <span>←</span>
                    Back to Resume
                  </button>
                </div>
              </div>
            </div>
          );
        }
        return (
          <ParsedResumeStep
            resumeData={resumeData}
            selectedTemplateId={selectedTemplateId}
            onNext={(updatedResumeData) => handleStepNext(updatedResumeData)}
            onBack={handleStepBack}
            onSave={(data) => setResumeData(data)}
            onStartNew={handleStartNew}
            isGuest={isGuest}
          />
        );
      
      case 3:
        return (
          <JobUrlStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            jobData={jobData || undefined}
            onJobParse={handleJobParse}
            isLoading={isLoading || jobHook.isScrapingJob}
            error={error || jobHook.scrapingError}
          />
        );
      
      case 4:
        // Show loading state while baseline analysis is being calculated – same card style as Upload/JobUrl steps
        if (!baselineScores && isLoading) {
          return (
            <div className={STEP_MOTION_CLASS}>
              <div className={STEP_WRAPPER_CLASS}>
                <div className={`${STEP_CARD_CLASS} items-center justify-center p-8 sm:p-12`}>
                  <div className="mb-6 h-12 w-12 animate-spin rounded-full border-2 border-solid border-t-[#6C47FF] dark:border-t-[#5B8DFF] vscode-border" />
                  <p className="vscode-text-primary text-lg font-medium">Analyzing resume and job match...</p>
                </div>
                <div className="mt-4 flex justify-start pt-4">
                  <button
                    type="button"
                    onClick={handleStepBack}
                    className="vscode-text-secondary hover:vscode-text-primary flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
                  >
                    <span>←</span>
                    Back to Resume
                  </button>
                </div>
              </div>
            </div>
          );
        }

        // Start Analysis prompt – same card style as previous steps
        if (!baselineScores) {
          const missingData = !resumeData || !jobData;
          return (
            <div className={STEP_MOTION_CLASS}>
              <div className={STEP_WRAPPER_CLASS}>
                <div className={`${STEP_CARD_CLASS} items-center justify-center p-8 sm:p-12`}>
                  {missingData ? (
                    <>
                      <p className="vscode-text-primary mb-6 text-center text-lg font-medium">
                        Resume and job data are required for analysis. Please complete the previous steps.
                      </p>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(!resumeData ? 0 : isRoleFitFlow ? 0 : 2)}
                        className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ background: 'var(--brand-cta-bg)' }}
                      >
                        <ArrowLeft className="h-4 w-4" />
                        {!resumeData ? 'Back to Resume' : isRoleFitFlow ? 'Back to resume & template' : 'Back to Job URL'}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="vscode-text-primary mb-6 text-center text-lg font-medium">Analyzing job match compatibility...</p>
                      <button
                        type="button"
                        onClick={handleBaselineAnalysis}
                        className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ background: 'var(--brand-cta-bg)' }}
                      >
                        [Start Analysis]
                      </button>
                    </>
                  )}
                </div>
                <div className="mt-4 flex justify-start pt-4">
                  <button
                    type="button"
                    onClick={handleStepBack}
                    className="vscode-text-secondary hover:vscode-text-primary flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
                  >
                    <span>←</span>
                    Back to Resume
                  </button>
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <BaselineAnalysisStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            resumeData={resumeData!}
            jobData={jobData || undefined}
            baselineScores={baselineScores}
            onOptimize={(preferences: any) => handleOptimize('openai-gpt-4o', preferences)}
            isLoading={isLoading}
            error={error}
          />
        );
      
      case 5:
        return (
          <OptimizationSettingsStep
            onNext={(model: string, preferences: any) => handleOptimize(model, preferences)}
            onBack={handleStepBack}
            jobData={jobData || undefined}
            hasEnoughCredits={credits === null || credits > 0}
            isLoading={isLoading}
            error={error}
          />
        );
      
      case 6:
        // Guard: if optimization is already done (e.g. restored from session or user
        // navigated back), skip straight to results — never re-run the AI.
        if (optimizationResult) {
          // Use a one-shot effect to advance without triggering an extra render cycle
          setTimeout(() => setCurrentStep(7), 0);
          return null;
        }
        return (
          <ProcessingStep
            resumeData={resumeData || undefined}
            jobData={jobData || undefined}
            flowError={error}
            onRetry={() => {
              appLogger.debug('[ProcessingStep] Retry - back to settings');
              setCurrentStep(5);
            }}
            onComplete={(perSectionResults) => {
              // Per-section results arrive as Array<{title, content}>
              appLogger.debug('[ProcessingStep] onComplete with per-section results', perSectionResults?.length);

              // Reconstruct full resume text from ordered sections.
              // Use Title Case (not ALL-CAPS) so that parseResumeSections fallback
              // paths don't activate the hasAllCapsStyle flag, which disables
              // mixed-case header detection and collapses section boundaries.
              const toTitleCase = (s: string) =>
                s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
              const fullText = perSectionResults
                .map(s => `${toTitleCase(s.title)}\n${s.content}`)
                .join('\n\n');

              const beforeScore = baselineScores?.overall ?? 0;
              const afterScore = Math.min(100, Math.round(beforeScore * 1.25 + 15));

              // Use valid UUIDs — fall back to generated UUIDs so DB save never fails on format
              const isValidUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s));
              const safeResumeId = (resumeData?.id && isValidUuid(resumeData.id)) ? resumeData.id : crypto.randomUUID();
              const safeJobId = (jobData?.id && isValidUuid(String(jobData.id))) ? String(jobData.id) : crypto.randomUUID();

              const result: OptimizationResult = {
                id: `opt_${Date.now()}`,
                resumeId: safeResumeId,
                jobId: safeJobId,
                jobTitle: (jobData?.title as string) || 'Job Position',
                companyName: (jobData?.company as string) || 'Company',
                jobUrl: (jobData?.url as string) || undefined,
                model: 'gpt-4o-mini-per-section',
                optimizedText: fullText,
                optimizedResume: fullText,
                final_resume: fullText,
                optimized_resume: fullText,
                sections: perSectionResults,
                originalScore: beforeScore,
                optimizedScore: afterScore,
                beforeScores: baselineScores ?? {
                  formatting: beforeScore, keywords: beforeScore, structure: beforeScore,
                  overall: beforeScore, atsRating: 'Fair',
                },
                afterScores: {
                  formatting: Math.min(100, (baselineScores?.formatting ?? beforeScore) + 18),
                  keywords: Math.min(100, (baselineScores?.keywords ?? beforeScore) + 22),
                  structure: Math.min(100, (baselineScores?.structure ?? beforeScore) + 15),
                  overall: afterScore,
                  atsRating: afterScore >= 85 ? 'Excellent' : afterScore >= 70 ? 'Good' : 'Fair',
                },
                baselineScores: baselineScores,
                improvements: [
                  'Rewrote Professional Summary to target this specific role',
                  'Action verbs and results-focused bullets in Work Experience',
                  'Skills organised into ATS-friendly sub-categories',
                ],
                confidence: 0.92,
                processingTime: 0,
                completedAt: new Date(),
              };

              appLogger.debug('[ProcessingStep] OptimizationResult assembled', { sections: perSectionResults.length });
              // Show results immediately — don't wait for DB save
              setOptimizationResult(result);
              setCurrentStep(7);

              // Fire-and-forget: send resume_optimized email to keep user engaged
              supabase.auth.getSession().then(({ data: { session } }) => {
                if (!session) return;
                supabase.functions.invoke('send-funnel-email', {
                  body: {
                    type: 'resume_optimized',
                    user_id: session.user.id,
                    email: session.user.email,
                    job_title: (jobData?.title as string) || '',
                    company_name: (jobData?.company as string) || '',
                    before_score: beforeScore,
                    after_score: afterScore,
                  },
                }).then(({ data: d, error: e }) => {
                  if (e) appLogger.warn('[ProcessingStep] resume_optimized email error:', e);
                  else   appLogger.debug('[ProcessingStep] resume_optimized email sent ✅ id:', d?.resend_id);
                }).catch((err) => appLogger.warn('[ProcessingStep] resume_optimized email network error:', err));
              });

              // Save to DB in background so it appears in history.
              // Store the promise in bgSavePromiseRef so handleSave can await it
              // before deciding whether to create a new DB row (prevents duplicates).
              bgSaveIdRef.current = null;
              // Persist selectedTemplateId + sections so My Activities download matches Download Step exactly
              const resultToSave = {
                ...result,
                ai_insights: { ...(result.ai_insights ?? {}), selectedTemplateId },
              };
              bgSavePromiseRef.current = import('@soVendor/services/optimizationService')
                .then(({ saveComprehensiveOptimization }) =>
                  saveComprehensiveOptimization(safeResumeId, safeJobId, resultToSave)
                )
                .then((saved) => {
                  const dbId = saved.optimization_id ?? null;
                  appLogger.debug('[ProcessingStep] Saved to DB, optimization_id:', dbId);
                  bgSaveIdRef.current = dbId;
                  // Patch BOTH id and optimization_id so downstream steps
                  // (handleSave dedup check, ApplyStep resume_version_id) get the real UUID.
                  setOptimizationResult(prev => prev
                    ? {
                        ...prev,
                        id: dbId ?? prev.id,
                        optimization_id: dbId ?? undefined,
                        link_id: saved.link_id,
                        created_at: saved.created_at,
                      }
                    : prev
                  );
                  void import('@core/supabase/services/optimizationService').then(({ optimizationService }) => {
                    const appId = applicationIdRef.current;
                    if (!appId || !dbId || !result) return;
                    const match =
                      (result as { optimizedScore?: number; match_score?: number }).optimizedScore ??
                      (result as { match_score?: number }).match_score;
                    const initial =
                      (result as { originalScore?: number; initial_score?: number }).originalScore ??
                      (result as { initial_score?: number }).initial_score;
                    return optimizationService.syncFromSingleOptimizeFlow(appId, {
                      b2c_optimization_id: dbId,
                      match_score: match != null && !Number.isNaN(Number(match)) ? Number(match) : null,
                      initial_score: initial != null && !Number.isNaN(Number(initial)) ? Number(initial) : null,
                      fit_assessment: result.fit_assessment ?? null,
                      skill_gaps:
                        (result as { skillGaps?: unknown; skill_gaps?: unknown }).skillGaps ??
                        (result as { skill_gaps?: unknown }).skill_gaps ??
                        null,
                    });
                  }).catch(() => {});

                  if (
                    isRoleFitFlow &&
                    roleFitPrefetch?.candidateId &&
                    roleFitPrefetch?.jobId &&
                    user?.orgId &&
                    dbId
                  ) {
                    try {
                      const optResult = {
                        ...result,
                        id: dbId,
                        optimization_id: dbId,
                      } as OptimizationResult;
                      const payload = buildRolefitProjectionUpsertFromOptimization(
                        user.orgId,
                        '1:1',
                        roleFitPrefetch.candidateId,
                        roleFitPrefetch.jobId,
                        optResult,
                        dbId,
                        user.orgUserId ?? null
                      );
                      void rolefitProjectionService
                        .upsertPayload(payload)
                        .then(() => appLogger.debug('[RoleFit] Projection upserted after single optimize'))
                        .catch((e: unknown) =>
                          appLogger.warn('[RoleFit] Projection upsert failed:', e instanceof Error ? e.message : e)
                        );
                    } catch (e) {
                      appLogger.warn('[RoleFit] Projection upsert error:', e);
                    }
                  }

                  return dbId;
                })
                .catch((err) => {
                  appLogger.warn('[ProcessingStep] DB save failed (non-critical):', err?.message ?? err);
                  return null;
                });
            }}
            onCancel={() => setCurrentStep(5)}
          />
        );
      
      case 7:
        return (
          <ResultsStep
            optimizationHook={{
              optimizationResults: optimizationResult ? [optimizationResult] : [],
              currentResult: optimizationResult,
              resetOptimization: () => {
                setOptimizationResult(null);
                setCurrentStep(0);
              }
            }}
            resumeText={resumeData?.parsedText || ''}
            onRestart={() => {
              setCurrentStep(0);
              setResumeData(null);
              setJobData(null);
              setBaselineScores(null);
              setOptimizationResult(null);
            }}
            onBack={handleStepBack}
            isGuest={isGuest}
            onSignup={onSignupProp}
            jobData={jobData ? { title: jobData.title, company: jobData.company, skills: jobData.skills, requirements: jobData.requirements, description: jobData.description } : undefined}
            selectedTemplateId={selectedTemplateId}
            onTemplateChange={setSelectedTemplateId}
            onDesignChange={setSelectedDesign}
            isRoleFitFlow={isRoleFitFlow}
            onDownloadPdf={handleDownloadPdfFromResults}
            downloadPdfBusy={isLoading}
            onSaveToProfile={isRoleFitFlow ? handleRoleFitSaveToProfile : undefined}
            profileSaveDesign={selectedDesign}
            saveToProfileBusy={isLoading}
            onContinueToHistory={!isRoleFitFlow ? () => setCurrentStep(9) : undefined}
          />
        );
      
      case 8:
        return null;
      
      case 9:
        // Check if this is a guest optimization (cannot be saved to history)
        const isGuestOptimization = optimizationResult?.id?.startsWith('guest-') || false;
        
        return (
          <SaveHistoryStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            optimizationResult={optimizationResult!}
            onSave={handleSave}
            isLoading={isLoading}
            error={error}
            skipEnabled={isGuestOptimization}
            skipMessage={isGuestOptimization ? "This was a guest optimization. Download your resume and run a fresh optimization to save to history." : undefined}
          />
        );
      
      case 10:
        return (
          <ApplyStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            optimizationResult={optimizationResult!}
            jobData={jobData!}
            onApply={handleApply}
            isLoading={isLoading}
            error={error}
            onExitToDashboard={optimizationResult ? () => onComplete(optimizationResult, 'dashboard') : undefined}
            fromHistoryApply={restoringFromHistoryApply}
          />
        );
      
      case 11:
        return (
          <ApplicationTrackingStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            jobData={jobData!}
            applicationId={applicationId}
            isLoading={isLoading}
            error={error}
            onApplyForSelected={(_applicationIds) => setCurrentStep(10)}
            onNewOptimization={() => {
              // Clear autosave so a fresh optimization starts clean
              sessionStorage.removeItem(FLOW_SESSION_KEY);
              setCurrentStep(0);
              setResumeData(null);
              setJobData(null);
              setBaselineScores(null);
              setOptimizationResult(null);
              setApplicationId(null);
              setDownloadedFormat(null);
              setSavedVersionId(null);
            }}
          />
        );
      
      default:
        return null;
    }
  };

  const handleStartNew = () => {
    try { sessionStorage.removeItem(FLOW_SESSION_KEY); } catch (_) { /* ignore */ }
    setCurrentStep(0);
    setResumeData(null);
    setJobData(null);
    setBaselineScores(null);
    setOptimizationResult(null);
    setApplicationId(null);
    setDownloadedFormat(null);
    setSavedVersionId(null);
  };

  // Step 2 (parsed-resume) has its own full-height branded toolbar — collapse the outer chrome
  const isEditorStep = currentStep === 2;

  const content = (
    <div
      className={`h-full min-h-0 flex flex-col relative z-0 overflow-hidden`}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Start over — no step numbers (keeps UI consistent with product chrome) */}
      {currentStep > 0 && currentStep < 7 && !isEditorStep && !Capacitor.isNativePlatform() && (
        <div className="vscode-bg-secondary z-10 flex flex-shrink-0 justify-end border-b border-solid px-4 py-2 vscode-border">
          <button
            type="button"
            onClick={handleStartNew}
            className="vscode-text-primary flex min-h-[40px] items-center gap-1.5 rounded-lg border border-solid px-3 py-1.5 text-xs font-semibold transition-colors hover:opacity-90 touch-manipulation vscode-border"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            [Start New]
          </button>
        </div>
      )}
      {/* Step content – scrolls here; scrollbar hidden after nav strips removed */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden scrollbar-hide relative z-0" style={{ pointerEvents: 'auto' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentStep}-${i18n.language}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="min-h-full flex flex-col relative z-0 w-full"
            style={{ pointerEvents: 'auto' }}
          >
            {restoringFromHistoryApply ? (
              <div className={STEP_WRAPPER_CLASS}>
                <div className={STEP_CARD_CLASS + ' flex flex-col items-center justify-center gap-4 py-12 min-h-[200px]'}>
                  <LoadingSpinner size="lg" text="Opening job apply flow…" showText={true} />
                </div>
              </div>
            ) : (
              renderCurrentStep()
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Guest sign-up gate: after analysis, before optimization settings — suppressed on native (no URL navigation) */}
      {!Capacitor.isNativePlatform() && ReactDOM.createPortal(
        <AnimatePresence>
          {showGuestSignUpModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center p-0 sm:p-4 pb-0"
              onClick={() => setShowGuestSignUpModal(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 24 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 24 }}
                transition={{ duration: 0.2 }}
                className="vscode-bg-secondary max-w-sm w-full overflow-hidden rounded-t-2xl border border-solid shadow-xl sm:rounded-2xl pb-[env(safe-area-inset-bottom)] vscode-border"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="flex flex-shrink-0 items-center gap-3 px-5 py-4"
                  style={{ backgroundColor: 'var(--brand-popup-header)' }}
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15">
                    <Settings className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="min-w-0 flex-1 text-lg font-bold leading-tight text-white">
                    [{t('optimizer.baselineStep.signUpToContinueTitle')}]
                  </h3>
                </div>
                <div className="px-5 pb-0 pt-4">
                  <p className="vscode-text-secondary text-sm">{t('optimizer.baselineStep.signUpToContinueDesc')}</p>
                </div>
                <div className="vscode-text-secondary flex flex-wrap gap-x-4 gap-y-1 px-5 py-3 text-xs">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    {t('optimizer.downloadStep.benefitDownload')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    {t('optimizer.downloadStep.benefitTrack')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    {t('optimizer.downloadStep.benefitUnlimited')}
                  </span>
                </div>
                <div className="p-5 pt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        sessionStorage.setItem('redirect_after_login', '/optimize/optimize');
                        sessionStorage.setItem('optimize_after_login_step', '5');
                      } catch (_) { /* ignore */ }
                      setShowGuestSignUpModal(false);
                      onSignupProp?.();
                    }}
                    className="w-full flex items-center justify-center gap-2 min-h-[44px] px-5 py-3 text-sm font-semibold text-white rounded-lg transition-opacity hover:opacity-90 touch-manipulation"
                    style={{ background: 'var(--brand-cta-bg)' }}
                  >
                    {t('optimizer.downloadStep.signUpToDownloadBtn')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        sessionStorage.setItem('redirect_after_login', '/optimize/optimize');
                        sessionStorage.setItem('optimize_after_login_step', '5');
                      } catch (_) { /* ignore */ }
                      setShowGuestSignUpModal(false);
                      window.location.href = '/login';
                    }}
                    className="vscode-text-secondary hover:vscode-text-primary w-full min-h-[44px] rounded-lg py-3 text-sm font-medium transition-colors touch-manipulation"
                  >
                    {t('optimizer.downloadStep.alreadyHaveAccount')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* After match analysis: confirm full resume generation (RoleFit “No” returns to matrix/AI) */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {showPostBaselineGenerateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9997] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
              onClick={() => setShowPostBaselineGenerateModal(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 24 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 24 }}
                transition={{ duration: 0.2 }}
                className="vscode-bg-secondary max-h-[90vh] w-full max-w-md overflow-hidden rounded-t-2xl border border-solid shadow-xl sm:rounded-2xl vscode-border"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="flex flex-shrink-0 items-center gap-3 px-5 py-4"
                  style={{ backgroundColor: 'var(--brand-popup-header)' }}
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15">
                    <Wand2 className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="min-w-0 flex-1 text-lg font-bold leading-tight text-white">
                    [{t('optimizer.baselineStep.postBaselineGenerateTitle')}]
                  </h3>
                </div>
                <div className="p-5 pt-4">
                  <p className="vscode-text-secondary text-sm">
                    {t('optimizer.baselineStep.postBaselineGenerateDesc')}
                  </p>
                </div>
                <div className="flex flex-col gap-2 border-t border-solid p-4 sm:flex-row sm:justify-end vscode-border">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPostBaselineGenerateModal(false);
                      if (isRoleFitFlow && onRoleFitAfterDownload) {
                        try {
                          sessionStorage.removeItem(FLOW_SESSION_KEY);
                        } catch (_) {
                          /* ignore */
                        }
                        onRoleFitAfterDownload();
                      }
                    }}
                    className="vscode-text-primary order-2 flex min-h-[44px] w-full items-center justify-center rounded-xl border border-solid px-4 py-2.5 text-sm font-semibold transition-colors hover:opacity-90 sm:order-1 sm:w-auto vscode-border"
                  >
                    {t('optimizer.baselineStep.postBaselineGenerateNo')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPostBaselineGenerateModal(false);
                      setCurrentStep(5);
                    }}
                    className="order-1 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:order-2 sm:w-auto"
                    style={{ background: 'var(--brand-cta-bg)' }}
                  >
                    {t('optimizer.baselineStep.postBaselineGenerateYes')}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Popup after PDF download from Results — portal so motion parent transforms don't trap the fixed overlay */}
      {ReactDOM.createPortal(
        <AnimatePresence>
        <ApplyTrackModal
          isOpen={showApplyAfterDownloadModal}
          onClose={() => {
            setShowApplyAfterDownloadModal(false);
          }}
          onTrack={() => {
            setShowApplyAfterDownloadModal(false);
            setCurrentStep(10);
          }}
          onSkip={() => {
            setShowApplyAfterDownloadModal(false);
            setShowWhereToGoAfterDownloadModal(true);
          }}
          jobData={{
            jobTitle: jobData?.title,
            companyName: jobData?.company,
            jobUrl: jobData?.url,
          }}
        />
        </AnimatePresence>,
        document.body
      )}

      {/* "Where do you want to go?" — portal so motion parent transforms don't trap fixed overlay */}
      {ReactDOM.createPortal(
        <AnimatePresence>
        {showWhereToGoAfterDownloadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[3px] flex items-center justify-center z-[9999] p-4"
            style={{ pointerEvents: 'all' }}
            onClick={() => setShowWhereToGoAfterDownloadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[340px] bg-white dark:bg-[#1e1e1e] rounded-2xl overflow-hidden"
              style={{
                boxShadow: '0 24px 64px -8px rgba(0,0,0,0.28), 0 4px 16px -4px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.08)',
              }}
            >
              {/* ── Title bar strip (single solid color) ── */}
              <div
                className="flex items-center gap-3 px-4 py-3.5"
                style={{ backgroundColor: 'var(--brand-popup-header)' }}
              >
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-white" fill="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-medium text-white/80 leading-none mb-0.5 uppercase tracking-wide">{t('optimizer.flow.resumeOptimizer')}</p>
                  <h2 className="text-sm font-semibold text-white leading-tight">{t('optimizer.flow.whatsYourNextStepBracketed')}</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowWhereToGoAfterDownloadModal(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors touch-manipulation shrink-0"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* ── Option list: flow next steps (Prepare, My Activities, AI Companion, New optimization, Dashboard) ── */}
              <div className="p-2">
                {[
                  {
                    icon: <Video className="w-[18px] h-[18px]" />,
                    label: t('optimizer.flow.prepareForInterview'),
                    desc: t('optimizer.flow.prepareDesc'),
                    onClick: () => {
                      setShowWhereToGoAfterDownloadModal(false);
                      onComplete(optimizationResult ?? ({} as OptimizationResult), 'prepare-for-interview');
                    },
                  },
                  {
                    icon: <History className="w-[18px] h-[18px]" />,
                    label: 'My Activities',
                    desc: 'View optimizations and applications',
                    onClick: () => {
                      setShowWhereToGoAfterDownloadModal(false);
                      onComplete(optimizationResult ?? ({} as OptimizationResult), 'history');
                    },
                  },
                  {
                    icon: <MessageCircle className="w-[18px] h-[18px]" />,
                    label: t('optimizer.flow.aiCompanion'),
                    desc: t('optimizer.flow.aiCompanionDesc'),
                    onClick: () => {
                      setShowWhereToGoAfterDownloadModal(false);
                      if (optimizationResult && jobData) {
                        setCompanionRecentContext({
                          source: 'post_optimization',
                          jobTitle: jobData.title,
                          company: jobData.company,
                          atsScore: optimizationResult.afterScores?.overall ?? optimizationResult.optimizedScore,
                          improvementsSummary: optimizationResult.improvements?.slice(0, 2).join('; '),
                        });
                      }
                      onComplete(optimizationResult ?? ({} as OptimizationResult), 'ai-companion');
                    },
                  },
                  {
                    icon: <Wand2 className="w-[18px] h-[18px]" />,
                    label: t('optimizer.flow.startNewOptimization'),
                    desc: t('optimizer.flow.startNewOptimizationDesc'),
                    onClick: () => {
                      setShowWhereToGoAfterDownloadModal(false);
                      setOptimizationResult(null);
                      setResumeData(null);
                      setJobData(null);
                      setBaselineScores(null);
                      setCurrentStep(0);
                    },
                  },
                  {
                    icon: <Home className="w-[18px] h-[18px]" />,
                    label: t('optimizer.flow.goToDashboard'),
                    desc: t('optimizer.flow.goToDashboardDesc'),
                    onClick: () => { setShowWhereToGoAfterDownloadModal(false); onBack(); },
                  },
                ].map((opt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={opt.onClick}
                    className="group flex w-full min-h-[44px] touch-manipulation items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:opacity-90"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white bg-[#5B3FFF]"
                    >
                      {opt.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="vscode-text-primary text-[13px] font-semibold leading-tight">[{opt.label}]</p>
                      <p className="vscode-text-secondary mt-0.5 text-[11px] leading-tight">{opt.desc}</p>
                    </div>
                    <ChevronRight className="vscode-text-tertiary h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>

              {/* ── Bottom caption ── */}
              <div className="px-4 pb-3 pt-0.5">
                <p className="vscode-text-tertiary text-center text-[10px]">Your optimized resume has been downloaded</p>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}

      {/* Feedback once per session when user completes the flow (step 10 → What's next?) */}
      <AnimatePresence>
      </AnimatePresence>

      {/* Post–flow next action: choose where to go (dashboard, history, AI companion, etc.) */}
      <NextActionModal
        isOpen={showNextActionModal}
        onClose={() => setShowNextActionModal(false)}
        onAction={(page) => {
          setShowNextActionModal(false);
          if (page === 'optimize-another') {
            // Reset entire flow for a fresh optimization
            sessionStorage.removeItem(FLOW_SESSION_KEY);
            setCurrentStep(0);
            setResumeData(null);
            setJobData(null);
            setBaselineScores(null);
            setOptimizationResult(null);
            setApplicationId(null);
            setDownloadedFormat(null);
            setSavedVersionId(null);
            return;
          }
          if (page === 'ai-companion' && optimizationResult && jobData) {
            setCompanionRecentContext({
              source: 'post_optimization',
              jobTitle: jobData.title,
              company: jobData.company,
              atsScore: optimizationResult.afterScores?.overall ?? optimizationResult.optimizedScore,
              improvementsSummary: optimizationResult.improvements?.slice(0, 2).join('; '),
            });
          }
          // Navigate to the selected tab; parent will switch content and URL
          onComplete(optimizationResult ?? ({} as OptimizationResult), page);
        }}
        optimizationResult={optimizationResult}
        jobData={jobData}
      />
    </div>
  );
  return onNavigate ? <OptimizerNavProvider onNavigate={onNavigate}>{content}</OptimizerNavProvider> : content;
};

export default SingleOptimizeFlow;
